import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Unlock, ShieldCheck, ArrowUpRight, Search, RefreshCw } from "lucide-react";

const FRAMEWORKS = ["BNBD", "PCEI", "PCAB"];

export default function FrameworkLocks() {
  const [globalRows, setGlobalRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: g }, { data: u }] = await Promise.all([
        api.get("/admin/framework-locks/global"),
        api.get("/users"),
      ]);
      setGlobalRows(g.items || []);
      setUsers(u.items || u || []);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };
  useEffect(() => { load(); }, []);

  const setGlobal = async (framework, is_locked) => {
    setBusy(true);
    try {
      await api.put("/admin/framework-locks/global", { framework, is_locked });
      toast.success(`${framework} ${is_locked ? "locked" : "unlocked"} globally`);
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const filtered = users.filter((u) =>
    q === "" || (`${u.name} ${u.email} ${u.company_name || ""}`).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="px-6 py-6 space-y-6">
      <header className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted">Admin · Access Control</div>
          <h1 className="text-3xl font-display font-bold tracking-tight mt-1 flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-brand" /> Framework Access
          </h1>
        </div>
        <button onClick={load} className="text-xs text-brand inline-flex items-center gap-1 hover:underline">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </header>

      {/* TESM info */}
      <div className="card-flat p-5 bg-good-soft/40 border-l-4 border-good text-sm" data-testid="tesm-always-open-banner">
        <span className="font-display font-bold text-good">TESM is always open</span> — not lockable at any level (per spec §2.1).
      </div>

      {/* Global defaults */}
      <div className="card-flat p-6">
        <div className="stat-label mb-1">Global Default</div>
        <div className="text-[11px] text-ink-muted uppercase tracking-wider mb-4">Sets the default for all users. Overridden by per-user settings below.</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="global-locks-grid">
          {FRAMEWORKS.map((fw) => {
            const row = globalRows.find((r) => r.framework === fw) || { is_locked: true };
            return (
              <div key={fw} className="border border-ink-line rounded-sm p-4 bg-white" data-testid={`global-${fw}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold text-lg">{fw}</div>
                  {row.is_locked ? <Lock className="w-4 h-4 text-warn" /> : <Unlock className="w-4 h-4 text-good" />}
                </div>
                <select value={row.is_locked ? "locked" : "unlocked"} disabled={busy}
                  onChange={(e) => setGlobal(fw, e.target.value === "locked")}
                  data-testid={`global-${fw}-select`}
                  className="mt-3 w-full px-2 py-1.5 border border-ink-line bg-white rounded-sm text-sm font-num focus:outline-none focus:ring-2 focus:ring-brand/20">
                  <option value="locked">Locked</option>
                  <option value="unlocked">Unlocked</option>
                </select>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-user overrides — pick a user to edit */}
      <div className="card-flat p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="stat-label">Per-User Overrides</div>
            <div className="text-[11px] text-ink-muted uppercase tracking-wider mt-0.5">Choose a user to set individual overrides. Individual override → Global → Locked default.</div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" data-testid="locks-user-search"
              className="pl-9 pr-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20" />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[11px] uppercase tracking-wider text-ink-muted">
            <tr><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Company</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-ink-line hover:bg-secondary/40" data-testid={`locks-user-row-${u.id}`}>
                <td className="px-3 py-2 font-medium">{u.name || "—"}</td>
                <td className="px-3 py-2 text-ink-muted">{u.email}</td>
                <td className="px-3 py-2 text-ink-muted text-xs">{u.company_name || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/admin/framework-locks/${u.id}`} data-testid={`locks-edit-${u.id}`}
                    className="text-xs text-brand inline-flex items-center gap-1 hover:underline">Manage <ArrowUpRight className="w-3 h-3" /></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
